import {
  Button,
  Card,
  EButtonColorVariant,
  ECardMediaSize,
  EHeadingStyleVariant,
  ELinkVariant,
  ETextStyleVariant,
  Heading,
  Link,
  Text,
} from '@outdoorsyco/bonfire';
import Image, { StaticImageData } from 'next/image';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import news1 from '@/assets/about/outdoorsy-group/news-1.jpg';
import news2 from '@/assets/about/outdoorsy-group/news-2.jpg';
import news3 from '@/assets/about/outdoorsy-group/news-3.jpg';
import news4 from '@/assets/about/outdoorsy-group/news-4.jpg';
import MaxWidth from '@/components/common/layout/MaxWidth';
import { useBreakpoint } from '@/hooks/useBreakpoint';

interface INewsItem {
  imghref: StaticImageData;
  text: string;
  href: string;
  description?: string;
}

const InTheNews = () => {
  const intl = useIntl();
  const { isMobile, isAboveTablet, isAboveDesktop } = useBreakpoint();

  const newsItems: INewsItem[] = [
    {
      imghref: news1,
      text: intl.formatMessage({
        defaultMessage: `The Outdoorsy Group Exceeds $3 Billion in Sales, Plans European Expansion`,
        id: 'u1mH6Q',
      }),
      href: 'https://www.prnewswire.com/news-releases/the-outdoorsy-group-exceeds-3-billion-in-sales-plans-european-expansion-302255696.html',
    },
    {
      imghref: news2,
      text: intl.formatMessage({
        defaultMessage: `“You Won't Be Roughing It at These Outdoor Resorts”`,
        id: 'Kv7Xm3',
      }),
      href: 'https://www.nytimes.com/2024/07/02/travel/glamping-outdoor-resorts.html',
      description: intl.formatMessage({
        defaultMessage: 'Outdoorsy Hill Country in the NYT',
        id: 'hsqnBu',
      }),
    },
    {
      imghref: news3,
      text: intl.formatMessage({ defaultMessage: 'Generations in the Wild', id: 'OrieFR' }),
      href: '/generations-in-the-wild-24',
      description: intl.formatMessage({
        defaultMessage: 'Outdoorsy launches first national research project',
        id: 'U5jYGW',
      }),
    },
    {
      imghref: news4,
      text: intl.formatMessage({
        defaultMessage:
          'Outdoorsy Group co-founder, Jen Young nominated to Inc 250 Female Business leaders',
        id: '5BNEyz',
      }),
      href: 'https://www.prnewswire.com/news-releases/outdoorsy-co-founder-jen-young-honored-in-incs-2024-female-founders-250-list-302112045.html',
    },
  ];

  let sectionHeading = EHeadingStyleVariant.H2Alt;

  if (isAboveTablet) {
    sectionHeading = EHeadingStyleVariant.H4Alt;
  }

  if (isAboveDesktop) {
    sectionHeading = EHeadingStyleVariant.H3Alt;
  }

  return (
    <section className="py-20 bg-canvas-100 md:py-24">
      <MaxWidth className="justify-center px-6 mx-auto">
        <Heading className="pb-6 md:pb-20" variant={sectionHeading}>
          <FormattedMessage defaultMessage="In the news" id="WIKWt+" />
        </Heading>

        <div className="flex flex-col max-w-full overflow-x-auto gap-x-6 gap-y-6 md:gap-y-0 md:grid grid-flow-col gap-4 lg:grid-cols-4 lg:grid auto-cols-[270px]">
          {newsItems.map(item => {
            return (
              <div className="max-w-[400px]" key={item.href}>
                <Card className="h-full !rounded-none" withBoxShadow={false}>
                  <Card.Media size={ECardMediaSize.Medium} className="overflow-clip">
                    <Image
                      src={item.imghref}
                      alt={item.text}
                      layout="fill"
                      objectFit="cover"
                      objectPosition="center"
                      loading="lazy"
                    />
                  </Card.Media>
                  <Card.Content className="p-8 md:p-4">
                    <Heading variant={isMobile ? EHeadingStyleVariant.H4 : EHeadingStyleVariant.H6}>
                      {item.text}
                    </Heading>
                    {item.description && (
                      <Text className="pt-4" variant={ETextStyleVariant.SmallRegular}>
                        {item.description}
                      </Text>
                    )}
                    <div className="pt-4">
                      <Link
                        href={item.href}
                        target="blank"
                        variant={ELinkVariant.Underline}
                        textVariant={ETextStyleVariant.SmallLink}
                        label={intl.formatMessage({ defaultMessage: 'Read more', id: 'S++WdB' })}
                      />
                    </div>
                  </Card.Content>
                </Card>
              </div>
            );
          })}
        </div>

        <Button
          className="self-center mt-10 w-fit lg:mt-20"
          href="/press"
          variant={EButtonColorVariant.Secondary}
          label={intl.formatMessage({ defaultMessage: 'View all press releases', id: 'SciFV1' })}
        />
      </MaxWidth>
    </section>
  );
};

export default InTheNews;
